import { waitForRecorderLease, waitForWriterQuiet } from './recorderLease';
import { RECORDER_SIGN_OUT_ACK_WINDOW_MS, RECORDER_SIGN_OUT_LEASE_TIMEOUT_MS } from './recorderTiming';

type SignOutHandler = {
  prepare: () => Promise<void>;
  cancel: () => void;
};

type RecorderControlMessage = {
  type: 'prepare-sign-out' | 'cancel-sign-out' | 'sign-out-heard' | 'sign-out-settled';
  /** Which sign-out an answer belongs to. */
  nonce?: string;
  /** Which tab is answering, so two tabs are two answers. */
  tab?: string;
};

let handler: SignOutHandler | null = null;
let channel: BroadcastChannel | null = null;
/** This tab, for the lifetime of this module. */
const tabId = `tab-${Math.random().toString(36).slice(2, 10)}`;

function post(message: RecorderControlMessage): void {
  try { channel?.postMessage(message); } catch { /* channel already closing */ }
}

export function registerRecorderSignOutHandler(nextHandler: SignOutHandler): () => void {
  handler = nextHandler;
  if (typeof BroadcastChannel !== 'undefined') {
    channel?.close();
    channel = new BroadcastChannel('vd-recorder-control-v1');
    channel.addEventListener('message', (event: MessageEvent<RecorderControlMessage>) => {
      if (event.data?.type === 'prepare-sign-out') {
        const { nonce } = event.data;
        /**
         * Answer first, then do the work.
         *
         * The signing-out tab is about to delete this account's recordings
         * from IndexedDB, and it has no way to know this tab exists until
         * this line runs. Answering before the teardown starts is what makes
         * the wait bounded by delivery rather than by the work.
         */
        post({ type: 'sign-out-heard', nonce, tab: tabId });
        void Promise.resolve(handler?.prepare())
          // Settled either way: a tab whose teardown failed has still stopped
          // writing, and one that never answers holds up the sign-out for
          // everybody until the timeout.
          .catch(() => undefined)
          .then(() => post({ type: 'sign-out-settled', nonce, tab: tabId }));
      }
      if (event.data?.type === 'cancel-sign-out') handler?.cancel();
    });
  }
  return () => {
    if (handler === nextHandler) handler = null;
    channel?.close();
    channel = null;
  };
}

/**
 * Wait for the other tabs to stop writing, if there are any.
 *
 * The recorder lease alone does not cover this: a tab that is finishing an
 * interrupted upload holds no lease, and purging IndexedDB underneath it
 * takes away the chunks it is still sending. So every tab that answers gets
 * waited for, and a tab that answers and then never settles costs the same
 * timeout as an unresponsive recorder.
 */
async function waitForOtherTabs(nonce: string, timeoutMs: number): Promise<void> {
  if (!channel) return;
  const live = channel;
  const heard = new Set<string>();
  const settled = new Set<string>();
  let finish: ((error?: Error) => void) | null = null;
  /**
   * Everyone who is going to answer has answered, and all of them are done.
   *
   * Both halves matter. Counting settlements against the tabs heard from so
   * far lets one quick tab end the wait while a slower one has not spoken
   * yet: it answers at thirteen milliseconds, the barrier opens, and the tab
   * that would have answered at eighty is still mid-upload when the purge
   * runs. So nothing completes before the discovery window has passed.
   */
  let discovered = false;
  const settledEveryone = () => {
    if (!discovered) return;
    if (heard.size === 0 || settled.size >= heard.size) finish?.();
  };
  const listener = (event: MessageEvent<RecorderControlMessage>) => {
    if (event.data?.nonce !== nonce || !event.data.tab) return;
    if (event.data.type === 'sign-out-heard') heard.add(event.data.tab);
    if (event.data.type === 'sign-out-settled') settled.add(event.data.tab);
    settledEveryone();
  };
  live.addEventListener('message', listener);
  try {
    await new Promise<void>((resolve, reject) => {
      const window = setTimeout(() => {
        discovered = true;
        settledEveryone();
      }, RECORDER_SIGN_OUT_ACK_WINDOW_MS);
      /**
       * The deadline refuses, rather than proceeding anyway.
       *
       * A tab said it heard the sign-out and never said it had finished. What
       * is on the other side of this wait is deleting this account's
       * recordings from local storage, and doing that while a tab is still
       * sending one loses it. An unresponsive recorder already fails sign-out
       * for the same reason; this is the same answer for a tab that is only
       * uploading. A tab closed mid-teardown ends up here too, and the retry
       * a person makes a moment later has nobody left to wait for.
       */
      const deadline = setTimeout(() => {
        finish?.(new Error('Another Toado tab did not finish putting its recording away in time.'));
      }, timeoutMs);
      finish = (error?: Error) => {
        clearTimeout(window);
        clearTimeout(deadline);
        if (error) reject(error);
        else resolve();
      };
    });
  } finally {
    live.removeEventListener('message', listener);
  }
}

/**
 * Stop this tab and notify every same-origin tab, then prove the owner lock is
 * free before IndexedDB is purged. This prevents a remote tab from committing
 * another chunk after sign-out cleanup.
 */
export async function prepareRecorderForSignOut(
  ownerId?: string,
  leaseTimeoutMs = RECORDER_SIGN_OUT_LEASE_TIMEOUT_MS,
): Promise<() => void> {
  const nonce = `so-${Math.random().toString(36).slice(2, 10)}`;
  const others = waitForOtherTabs(nonce, leaseTimeoutMs);
  post({ type: 'prepare-sign-out', nonce });
  await handler?.prepare();
  await others;
  if (!ownerId) return () => {};
  /**
   * The question the acknowledgements cannot actually settle.
   *
   * A tab that has not answered is indistinguishable from a tab that is not
   * there, so no amount of waiting for answers proves nobody is writing. Every
   * writer holds the writers' lock shared; taking it exclusively is the
   * browser answering instead of a timer, and it is granted exactly when the
   * last writer lets go. The acknowledgements above are still worth sending -
   * they are what makes the other tabs stop - but this is the proof.
   */
  const writersQuiet = await waitForWriterQuiet(ownerId, leaseTimeoutMs);
  if (!writersQuiet) throw new Error('Another Toado tab is still saving a recording.');
  const release = await waitForRecorderLease(ownerId, leaseTimeoutMs);
  if (!release) {
    writersQuiet();
    throw new Error('Another recorder tab did not stop in time.');
  }
  return () => {
    release();
    writersQuiet();
  };
}

export function cancelRecorderSignOut(): void {
  post({ type: 'cancel-sign-out' });
  handler?.cancel();
}
